import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { ROUTES } from '@cloud-ui/constants';
import { useCurrentUserTeams } from '@cloud-ui/hooks/useTeams';
import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';
import type { TeamWithoutMembersDTO } from '@shared/dto/teams';

import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from './AuthContext';

interface TeamContextType {
  teams: TeamWithoutMembersDTO[] | undefined;
  currentTeam: TeamWithoutMembersDTO | null;
  setCurrentTeam: (team: TeamWithoutMembersDTO) => void;
  clearCurrentTeam: () => void;
  isLoading: boolean;
  /** Initial team discovery and selection for the current signed-in identity. */
  isInitializing: boolean;
}

export const TeamsContext = createContext<TeamContextType | undefined>(undefined);

export const TeamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: teams, isLoading: teamsLoading, fetchStatus } = useCurrentUserTeams();
  const [currentTeam, setCurrentTeam] = useState<TeamWithoutMembersDTO | null>(null);
  const [initializedFor, setInitializedFor] = useState<{
    userId: string | undefined;
    organizationId: string | undefined;
  } | null>(null);
  const { user, organization } = useAuth();
  // An identity change must stop exposing the previous team before effects run.
  const hasInitialized =
    initializedFor !== null &&
    initializedFor.userId === user?.id &&
    initializedFor.organizationId === organization?.id;
  // A later retry must not repeat the initial mount gate for an already initialized identity.
  const isInitializing = !!user && !!organization && !hasInitialized;

  // When query is disabled (no user), isLoading may be true but fetchStatus is 'idle'.
  // We should allow unauthenticated users to access public pages.
  const isActuallyLoading = teamsLoading && fetchStatus !== 'idle';

  // Reset state when user or organization changes
  useEffect(() => {
    setCurrentTeam(null);
    setInitializedFor(null);
  }, [user?.id, organization?.id]);

  useEffect(() => {
    const identity = { userId: user?.id, organizationId: organization?.id };
    if (teams?.length) {
      // First try to load from local storage
      const savedTeamId = localStorage.getItem(`${user?.id}-currentTeamId`);
      if (savedTeamId) {
        const savedTeam = teams.find((team) => team.id === savedTeamId);
        if (savedTeam) {
          setCurrentTeam(savedTeam);
          setInitializedFor(identity);
          return;
        }
      }
      // If no team in local storage or saved team not found, select the first team
      setCurrentTeam(teams[0]);
      if (user?.id) {
        localStorage.setItem(`${user.id}-currentTeamId`, teams[0].id);
      }
      setInitializedFor(identity);
    } else if (!isActuallyLoading && (teams?.length === 0 || teams === undefined)) {
      // An authoritative empty list means the previously selected team is no longer accessible.
      if (teams?.length === 0) {
        setCurrentTeam(null);
        tryBrowserStorage(() => localStorage.removeItem(`${user?.id}-currentTeamId`));
      }
      // If we have no teams (or query is disabled for unauthenticated users) and we're done loading,
      // mark as initialized to allow rendering of public pages
      setInitializedFor(identity);
    }
  }, [teams, isActuallyLoading, user?.id, organization?.id]);

  // Custom setter that also updates local storage
  const handleSetCurrentTeam = (team: TeamWithoutMembersDTO) => {
    setCurrentTeam(team);
    localStorage.setItem(`${user?.id}-currentTeamId`, team.id);
  };

  const clearCurrentTeam = () => {
    setCurrentTeam(null);
    tryBrowserStorage(() => localStorage.removeItem(`${user?.id}-currentTeamId`));
  };

  return (
    <TeamsContext.Provider
      value={{
        teams,
        currentTeam: hasInitialized ? currentTeam : null,
        setCurrentTeam: handleSetCurrentTeam,
        clearCurrentTeam,
        isLoading: isActuallyLoading || isInitializing,
        isInitializing,
      }}
    >
      {children}
    </TeamsContext.Provider>
  );
};

export const useTeamsContext = () => {
  const context = useContext(TeamsContext);
  if (context === undefined) {
    throw new Error('useTeamsContext must be used within an TeamsProvider');
  }
  return context;
};

export const useNavigateOnTeamChange = (isNew: boolean, navigateToPath: string) => {
  const { currentTeam } = useTeamsContext();
  const navigate = useNavigate();
  const initialTeamId = useRef(currentTeam?.id);

  useEffect(() => {
    // Only redirect if:
    // 1. We're not creating a new entity (isNew is false)
    // 2. We have a current team
    // 3. We have an initial team ID (not the first render)
    // 4. The current team ID is different from the initial team ID
    if (
      currentTeam &&
      !isNew &&
      initialTeamId.current !== undefined &&
      initialTeamId.current !== currentTeam.id
    ) {
      navigate(navigateToPath);
    }

    // Update the ref after the first render
    if (initialTeamId.current === undefined && currentTeam) {
      initialTeamId.current = currentTeam.id;
    }
  }, [currentTeam, isNew, navigate, navigateToPath]);
};

export const RequireSelectedTeam: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { currentTeam, isLoading } = useTeamsContext();
  const location = useLocation();

  const exemptRoutes = [
    ROUTES.login,
    ROUTES.verifyEmail,
    ROUTES.passwordReset.request,
    ROUTES.passwordReset.confirm,
    ROUTES.upgrade,
    ROUTES.noTeam,
    ROUTES.adminJobs,
    ...Object.values(ROUTES.organization).flat(),

    // These are exempt because they can be public, the redirect happens in the components
    ROUTES.eval,
    ROUTES.redteam.report,
  ];

  if (isLoading) {
    return;
  }

  // Check if current path requires a team selection
  const currentPath = location.pathname;

  const exempt =
    exemptRoutes.length === 0 || // If no routes specified, require team everywhere
    exemptRoutes.some((route) => currentPath === route || currentPath.startsWith(`${route}/`));

  if (!currentTeam && !exempt) {
    return <Navigate to={ROUTES.noTeam} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
